import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { getGlobalStats } from '@/lib/api'
import { PageMotion, Stagger, StaggerItem } from '@/components/Motion'
import { EmptyState, ErrorBlock, LoadingBlock, PageHeader, Panel, StatStrip } from '@/components/ui'
import { formatNum, sideColor } from '@/lib/utils'
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ArrowUpRight } from 'lucide-react'

const chartTooltip = {
  background: '#222222',
  border: '1px solid #2e2e2e',
  borderRadius: 6,
  fontSize: 12,
  color: '#e5e5e5',
}

export default function Dashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['stats'],
    queryFn: getGlobalStats,
  })

  if (isLoading) return <LoadingBlock />
  if (error || !data) return <ErrorBlock message="Failed to load stats" />

  const weaponChart = (data.topWeapons ?? []).slice(0, 8).map((w) => ({
    name: w.weapon?.length > 18 ? w.weapon.slice(0, 16) + '…' : w.weapon,
    full: w.weapon,
    count: w.count,
  }))

  return (
    <PageMotion className="space-y-8">
      <PageHeader
        title="Dashboard"
        subtitle="Mission intelligence across the community"
      />

      <StatStrip
        items={[
          { label: 'Missions', value: data.totalMissions },
          { label: 'Kills', value: data.totalKills },
          { label: 'Players', value: data.totalPlayers },
          {
            label: 'Top weapon',
            value: data.mostLethalWeapon?.weapon || '—',
            hint: data.mostLethalWeapon?.count
              ? `${formatNum(data.mostLethalWeapon.count)} kills`
              : undefined,
          },
        ]}
      />

      <Stagger className="grid gap-4 lg:grid-cols-2">
        <StaggerItem>
          <Panel title="Top killers">
            {(data.topPlayers ?? []).length === 0 ? (
              <EmptyState message="No kill data yet" />
            ) : (
              <ol className="space-y-0.5">
                {data.topPlayers.map((p, i) => (
                  <li key={p.playerId}>
                    <Link
                      to={`/players/${p.playerId}`}
                      className="group flex items-center justify-between gap-3 rounded-control px-3 py-2.5 transition-colors hover:bg-accent-soft"
                    >
                      <span className="flex items-center gap-3 min-w-0">
                        <span className="font-mono text-xs text-ink-faint w-5 tabular-nums">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span className="truncate text-ink group-hover:text-accent-text transition-colors">
                          {p.name}
                        </span>
                      </span>
                      <span className="font-mono text-accent-text tabular-nums">
                        {formatNum(p.kills)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            )}
          </Panel>
        </StaggerItem>

        <StaggerItem>
          <Panel title="Weapons">
            {weaponChart.length === 0 ? (
              <EmptyState message="No weapon data" />
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weaponChart} layout="vertical" margin={{ left: 4, right: 8 }}>
                    <XAxis type="number" hide />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={110}
                      tick={{ fill: '#a3a3a3', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={chartTooltip}
                      labelFormatter={(_, payload) =>
                        (payload?.[0]?.payload as { full?: string })?.full ?? ''
                      }
                    />
                    <Bar dataKey="count" fill="#8b9a7c" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>
        </StaggerItem>

        <StaggerItem>
          <Panel title="Maps">
            {(data.topMaps ?? []).length === 0 ? (
              <EmptyState message="No maps yet" />
            ) : (
              <ul className="space-y-2.5">
                {data.topMaps.map((m) => {
                  const max = Math.max(...data.topMaps.map((x) => x.count ?? 0), 1)
                  const pct = ((m.count ?? 0) / max) * 100
                  return (
                    <li key={m.worldname}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-ink">{m.worldname || 'Unknown'}</span>
                        <span className="font-mono text-ink-muted">{formatNum(m.count ?? 0)}</span>
                      </div>
                      <div className="h-1 rounded-full bg-line-soft overflow-hidden">
                        <div className="h-full rounded-full bg-accent/80" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </Panel>
        </StaggerItem>

        <StaggerItem>
          <Panel title="Side distribution">
            {(data.sideDistribution ?? []).length === 0 ? (
              <EmptyState message="No side data" />
            ) : (
              <div className="space-y-3">
                {data.sideDistribution.map((s) => {
                  const total = data.sideDistribution.reduce((a, b) => a + b.count, 0) || 1
                  const pct = (s.count / total) * 100
                  return (
                    <div key={s.side}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className={sideColor(s.side)}>{s.side || 'UNKNOWN'}</span>
                        <span className="font-mono text-ink-muted">{formatNum(s.count)}</span>
                      </div>
                      <div className="h-1 rounded-full bg-line-soft overflow-hidden">
                        <div className="h-full rounded-full bg-accent/70" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </Panel>
        </StaggerItem>
      </Stagger>

      <Panel
        title="Recent missions"
        action={
          <Link
            to="/missions"
            className="inline-flex items-center gap-1 text-xs text-accent-text hover:underline"
          >
            View all <ArrowUpRight size={12} />
          </Link>
        }
        flush
      >
        {(data.recentMissions ?? []).length === 0 ? (
          <div className="p-4">
            <EmptyState message="No missions yet" />
          </div>
        ) : (
          <div className="table-wrap border-0 rounded-none">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Mission</th>
                  <th>Map</th>
                  <th>Victory</th>
                  <th>Start</th>
                </tr>
              </thead>
              <tbody>
                {data.recentMissions.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <Link to={`/missions/${m.id}`} className="link-accent font-medium">
                        {m.missionName || `Mission #${m.id}`}
                      </Link>
                    </td>
                    <td>{m.worldname}</td>
                    <td>{m.victory || '—'}</td>
                    <td className="font-mono text-xs text-ink-faint">{m.missionStart || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </PageMotion>
  )
}
